import React, { useState, useEffect, useCallback, useRef } from 'react';

const homepageSlides = [
  {
    id: 'arolozorov',
    src: '/content-arolozorov-50.webp',
    label: 'אלרוזורוב',
    pin: { top: '29%', left: '54%' }
  },
  {
    id: 'ben-yehuda',
    src: '/content-ben-yehuda-147.webp',
    label: 'בן יהודה',
    pin: { top: '21%', left: '46.3%' }
  },
  {
    id: 'frishman',
    src: '/content-frishman-47.webp',
    label: 'פרישמן',
    pin: { top: '78.5%', left: '52%' }
  },
  {
    id: 'hayarkon',
    src: '/content-hayarkon-136.webp',
    label: 'הירקון',
    pin: { top: '59%', left: '34%' }
  }
];

const AboutPage = ({ onTabChange }) => {
  // Slide carousel state
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isFadingSlide, setIsFadingSlide] = useState(false);
  const [scrollToBottom, setScrollToBottom] = useState(false);
  const scrollStartDelayMs = 800;
  const scrollDurationMs = 9000;
  const bottomPauseMs = 800;
  const fadeDurationMs = 600;
  const slideIntervalMs = scrollStartDelayMs + scrollDurationMs + bottomPauseMs + fadeDurationMs;
  const scrollTimerRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const nextTimerRef = useRef(null);
  const pauseTimerRef = useRef(null);

  const clearSlideTimers = () => {
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    if (nextTimerRef.current) clearTimeout(nextTimerRef.current);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
  };

  const startSlideCycle = useCallback(() => {
    clearSlideTimers();
    setIsFadingSlide(false);
    setScrollToBottom(false);

    scrollTimerRef.current = setTimeout(() => {
      setScrollToBottom(true);
    }, scrollStartDelayMs);

    fadeTimerRef.current = setTimeout(() => {
      setIsFadingSlide(true);
    }, scrollStartDelayMs + scrollDurationMs + bottomPauseMs);

    nextTimerRef.current = setTimeout(() => {
      setScrollToBottom(false);
      setActiveSlideIndex(prev => (prev + 1) % homepageSlides.length);
      setIsFadingSlide(false);
    }, slideIntervalMs);
  }, [bottomPauseMs, scrollDurationMs, slideIntervalMs]);

  useEffect(() => {
    startSlideCycle();
    return () => clearSlideTimers();
  }, [activeSlideIndex, startSlideCycle]);

  return (
    <div className="about-page">
      {/* Hero: Welcome + product description */}
      <section className="homepage-hero">
        <div className="homepage-hero-inner" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/laptop-bg-map.webp)` }}>
          <div className="laptop-column">
            <div className="homepage-hero-image">
              <img src="/laptop.webp" alt="מפה על גבי מחשב נייד" loading="lazy" />
              <img
                src="/map-pin.svg"
                alt="סימון מיקום"
                className="laptop-map-pin"
                style={homepageSlides[activeSlideIndex]?.pin}
              />
            </div>
            <div className="laptop-indicators" role="tablist" aria-label="החלפת תצוגות">
              {homepageSlides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  className={`browser-indicator ${index === activeSlideIndex ? 'is-active' : ''}`}
                  onClick={() => {
                    if (index === activeSlideIndex) return;
                    clearSlideTimers();
                    setIsFadingSlide(true);
                    setTimeout(() => {
                      setActiveSlideIndex(index);
                      setScrollToBottom(false);
                      setIsFadingSlide(false);
                      startSlideCycle();
                    }, fadeDurationMs);
                  }}
                  disabled={index === activeSlideIndex}
                  aria-label={`מעבר לתצוגה ${slide.label}`}
                  aria-pressed={index === activeSlideIndex}
                />
              ))}
            </div>
          </div>
          <div className="homepage-hero-text">
            <h2 className="hero-headline">ברוכים הבאים ל- UrbaniX<span className="hero-ai">.ai</span></h2>
            <p className="hero-body">
              <strong>מערכת חכמה</strong> המאתרת ומרכזת מידע מעולם הנדל"ן והבנייה,
              <br />
              ובעזרת <strong>בינה מלאכותית</strong> מסייעת בביצוע פעולות מורכבות
              <br />
              בקלות, בזריזות וביעילות - והכל <span className="hero-cta-text">...בלחיצת כפתור!</span>
            </p>
          </div>
        </div>
      </section>

      {/* Browser screenshot carousel */}
      <div className="browser-screenshot-section">
        <div className="browser-mockup">
          <img src="/browser-frame.png" alt="מסגרת דפדפן" className="browser-frame" />
          <div className={`browser-viewport ${isFadingSlide ? 'is-fading' : ''}`}>
            <img
              key={homepageSlides[activeSlideIndex]?.id}
              src={homepageSlides[activeSlideIndex]?.src}
              alt={`תוכן המערכת - ${homepageSlides[activeSlideIndex]?.label}`}
              className={`browser-content ${scrollToBottom ? 'is-scrolling' : ''}`}
              style={{ transitionDuration: `${scrollDurationMs / 1000}s` }}
            />
          </div>
          <div className="browser-hotspot browser-hotspot-left">
            <img src="/bubble-left.webp" alt="בועה שמאלית" className="browser-bubble browser-bubble-left" />
          </div>
          <div className="browser-hotspot browser-hotspot-right">
            <img src="/bubble-right.webp" alt="בועה ימנית" className="browser-bubble browser-bubble-right" />
          </div>
        </div>
      </div>

      {/* Supported cities */}
      <div className="supported-cities-section">
        <h3 className="supported-cities-title">עיריות נתמכות על-ידי אורבניקס</h3>
        <div className="divider-line"></div>
        <div className="supported-cities-logos">
          <div className="city-logo-wrapper">
            <img src="/tel-aviv-logo.svg" alt="עיריית תל אביב-יפו" className="city-logo" />
          </div>
          <div className="logo-separator">•</div>
          <div className="city-logo-wrapper">
            <img src="/jerusalem-logo.svg" alt="עיריית ירושלים" className="city-logo" />
          </div>
          <div className="logo-separator">•</div>
          <div className="city-logo-wrapper">
            <img src="/haifa-logo.png" alt="עיריית חיפה" className="city-logo" />
          </div>
          <div className="logo-separator">•</div>
          <div className="city-logo-wrapper">
            <img src="/ramat-gan-logo.svg" alt="עיריית רמת גן" className="city-logo" />
          </div>
          <div className="logo-separator">•</div>
          <div className="city-logo-wrapper">
            <img src="/givatayim-logo.png" alt="עיריית גבעתיים" className="city-logo" />
          </div>
        </div>
        <div className="supported-cities-more">+29</div>
        <p className="supported-cities-list">
          ראשון לציון, פתח תקווה, אשדוד, באר שבע, בת ים, חולון, בני ברק, הרצליה, רחובות, מודיעין, אשקלון ועוד...
        </p>
        <div className="divider-line"></div>
        <p className="supported-cities-description">
          <strong className="highlighted-text">העיר בה נמצא הנכס שלכם עדיין לא נמצא ברשימה? אל דאגה!</strong><br/>
          המערכת עדיין תנגיש עבורכם מידע תכנוני בסיסי מ-iPLAN ומקורות ממשלתיים.
        </p>
      </div>

      {/* Stay connected + newsletter */}
      <div className="stay-connected-section" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/bghomepagecontactus.webp)` }}>
        <div className="stay-connected-overlay"></div>
        <div className="stay-connected-content">
          <h2 className="stay-connected-title">הישארו מחוברים למערכת המידע של אורבניקס</h2>
          <p className="stay-connected-subtitle">
            אהבתם ונשאר לכם טעם של עוד? הישארו מחוברים ל- <span className="brand-highlight">Urbani<span className="brand-x">X</span><span className="brand-suffix">.ai</span></span>
          </p>
          <p className="stay-connected-description">
            ע״י הרשמה לאתר אורבניקס תוכלו לעקוב אחרי שינויים במידע על הנכסים שלכם - תוכניות חדשות,
            <br />
            פרויקטים בסביבת הנכס, עדכונים של הרשויות המקומיות והממשלתיות ועוד... ועוד... ועוד...
          </p>
          <button 
            className="btn btn-primary btn-lg"
            onClick={() => {
              if (onTabChange) {
                onTabChange('contact');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            צור קשר
          </button>
        </div>
        
      </div>
    </div>
  );
};

export default AboutPage;
